import { BaseState, StateManager } from '@app/store/shared';
import { createReducer, on } from '@ngrx/store';
import { learningPortfolioActions } from '../actions/learning-portfolio.actions';
import {
    createLearningPortfolioParamsKey,
    LearningPortfolioAttachment,
    LearningPortfolioCategory,
    LearningPortfolioNote,
    LearningPortfolioNoteList,
    LearningPortfolioPendingUpload,
} from '../models';

type NotesSlice = BaseState<LearningPortfolioNoteList>;

interface AttachmentsSlice {
    items: LearningPortfolioAttachment[];
    pending: LearningPortfolioPendingUpload[];
    failed: boolean;
}

export interface LearningPortfolioState {
    notes: Record<string, NotesSlice>;
    notesKey: string | null;
    note: Record<string, BaseState<LearningPortfolioNote>>;
    categories: BaseState<LearningPortfolioCategory[]>;
    categoriesSaving: boolean;
    createdCategory: LearningPortfolioCategory | null;
    attachments: AttachmentsSlice;
    saving: boolean;
    saved: boolean | null;
    deleted: boolean | null;
}

const initialAttachments: AttachmentsSlice = {
    items: [],
    pending: [],
    failed: false,
};

const initialState: LearningPortfolioState = {
    notes: {},
    notesKey: null,
    note: {},
    categories: StateManager.initial(),
    categoriesSaving: false,
    createdCategory: null,
    attachments: initialAttachments,
    saving: false,
    saved: null,
    deleted: null,
};

function removePending(pending: LearningPortfolioPendingUpload[], uploadId: string): LearningPortfolioPendingUpload[] {
    return pending.filter((upload) => upload.uploadId !== uploadId);
}

function forgetNote(cache: Record<string, BaseState<LearningPortfolioNote>>, id: string): Record<string, BaseState<LearningPortfolioNote>> {
    return Object.fromEntries(Object.entries(cache).filter(([cachedId]) => cachedId !== id));
}

function replaceNote(slice: NotesSlice, note: LearningPortfolioNote): NotesSlice {
    const data = slice.data;

    if (!data) {
        return slice;
    }

    return { ...slice, data: { ...data, items: data.items.map((item) => (item.id === note.id ? note : item)) } };
}

export const learningPortfolioReducer = createReducer(
    initialState,

    on(learningPortfolioActions.getNotes, (state, { params }): LearningPortfolioState => {
        const key = createLearningPortfolioParamsKey(params);
        const slice = state.notes[key] ?? StateManager.initial<LearningPortfolioNoteList>();

        return {
            ...state,
            notesKey: key,
            // A cached slice stays visible while it refreshes, so going back to a seen filter shows no loader.
            notes: { ...state.notes, [key]: StateManager.setLoadingInitOnly(slice) },
        };
    }),
    on(learningPortfolioActions.getNotesSuccess, (state, { params, payload }): LearningPortfolioState => {
        const key = createLearningPortfolioParamsKey(params);
        const slice = state.notes[key] ?? StateManager.initial<LearningPortfolioNoteList>();

        return {
            ...state,
            notes: { ...state.notes, [key]: StateManager.setLoaded(slice, payload) },
        };
    }),
    on(learningPortfolioActions.getNotesError, (state, { params }): LearningPortfolioState => {
        const key = createLearningPortfolioParamsKey(params);
        const slice = state.notes[key] ?? StateManager.initial<LearningPortfolioNoteList>();

        return {
            ...state,
            notes: { ...state.notes, [key]: StateManager.setError(slice) },
        };
    }),

    on(learningPortfolioActions.getNote, (state, { id }): LearningPortfolioState => {
        const slice = state.note[id] ?? StateManager.initial<LearningPortfolioNote>();

        return {
            ...state,
            note: { ...state.note, [id]: StateManager.setLoadingInitOnly(slice) },
        };
    }),
    on(learningPortfolioActions.getNoteSuccess, (state, { payload }): LearningPortfolioState => {
        const slice = state.note[payload.id] ?? StateManager.initial<LearningPortfolioNote>();

        return {
            ...state,
            note: { ...state.note, [payload.id]: StateManager.setLoaded(slice, payload) },
        };
    }),
    on(learningPortfolioActions.getNoteError, (state, { id }): LearningPortfolioState => {
        const slice = state.note[id] ?? StateManager.initial<LearningPortfolioNote>();

        return {
            ...state,
            note: { ...state.note, [id]: StateManager.setError(slice) },
        };
    }),

    on(learningPortfolioActions.getCategories, (state): LearningPortfolioState => ({
        ...state,
        categories: StateManager.setLoading(state.categories),
    })),
    on(learningPortfolioActions.getCategoriesSuccess, (state, { payload }): LearningPortfolioState => ({
        ...state,
        categories: StateManager.setLoaded(state.categories, payload),
    })),
    on(learningPortfolioActions.getCategoriesError, (state): LearningPortfolioState => ({
        ...state,
        categories: StateManager.setError(state.categories),
    })),

    on(learningPortfolioActions.createCategory, (state): LearningPortfolioState => ({
        ...state,
        categoriesSaving: true,
        createdCategory: null,
    })),
    on(learningPortfolioActions.createCategorySuccess, (state, { payload }): LearningPortfolioState => {
        const current = state.categories.data ?? [];

        return {
            ...state,
            categoriesSaving: false,
            createdCategory: payload,
            categories: StateManager.setLoaded(state.categories, [...current, payload]),
        };
    }),
    on(learningPortfolioActions.createCategoryError, (state): LearningPortfolioState => ({
        ...state,
        categoriesSaving: false,
    })),

    on(learningPortfolioActions.createNote, (state): LearningPortfolioState => ({
        ...state,
        saving: true,
        saved: null,
    })),
    on(learningPortfolioActions.createNoteSuccess, (state): LearningPortfolioState => ({
        ...state,
        saving: false,
        saved: true,
        attachments: initialAttachments,
    })),
    on(learningPortfolioActions.createNoteError, (state): LearningPortfolioState => ({
        ...state,
        saving: false,
        saved: false,
    })),

    on(learningPortfolioActions.updateNote, (state): LearningPortfolioState => ({
        ...state,
        saving: true,
        saved: null,
    })),
    on(learningPortfolioActions.updateNoteSuccess, (state, { payload }): LearningPortfolioState => {
        const key = state.notesKey;
        const slice = key ? state.notes[key] : null;
        const cached = state.note[payload.id] ?? StateManager.initial<LearningPortfolioNote>();

        return {
            ...state,
            saving: false,
            saved: true,
            attachments: initialAttachments,
            note: { ...state.note, [payload.id]: StateManager.setLoaded(cached, payload) },
            notes: key && slice ? { [key]: replaceNote(slice, payload) } : {},
        };
    }),
    on(learningPortfolioActions.updateNoteError, (state): LearningPortfolioState => ({
        ...state,
        saving: false,
        saved: false,
    })),
    on(learningPortfolioActions.clearSaved, (state): LearningPortfolioState => ({
        ...state,
        saved: null,
    })),

    on(learningPortfolioActions.uploadAttachments, (state, { uploadId, files }): LearningPortfolioState => ({
        ...state,
        attachments: {
            ...state.attachments,
            pending: [...state.attachments.pending, ...files.map((file) => ({ uploadId, name: file.name }))],
            failed: false,
        },
    })),
    on(learningPortfolioActions.uploadAttachmentsSuccess, (state, { uploadId, payload }): LearningPortfolioState => ({
        ...state,
        attachments: {
            items: [...state.attachments.items, ...payload],
            pending: removePending(state.attachments.pending, uploadId),
            failed: state.attachments.failed,
        },
    })),
    on(learningPortfolioActions.uploadAttachmentsError, (state, { uploadId }): LearningPortfolioState => ({
        ...state,
        attachments: { ...state.attachments, pending: removePending(state.attachments.pending, uploadId), failed: true },
    })),
    on(learningPortfolioActions.removeAttachment, (state, { id }): LearningPortfolioState => ({
        ...state,
        attachments: { ...state.attachments, items: state.attachments.items.filter((attachment) => attachment.id !== id) },
    })),
    on(learningPortfolioActions.setAttachments, (state, { payload }): LearningPortfolioState => ({
        ...state,
        attachments: { ...initialAttachments, items: payload },
    })),
    on(learningPortfolioActions.clearAttachments, (state): LearningPortfolioState => ({
        ...state,
        attachments: initialAttachments,
    })),

    on(learningPortfolioActions.deleteNote, (state): LearningPortfolioState => ({
        ...state,
        deleted: null,
    })),
    on(learningPortfolioActions.deleteNoteSuccess, (state, { id }): LearningPortfolioState => ({
        ...state,
        deleted: true,
        note: forgetNote(state.note, id),
    })),
    on(learningPortfolioActions.deleteNoteError, (state): LearningPortfolioState => ({
        ...state,
        deleted: false,
    })),
    on(learningPortfolioActions.clearDeleted, (state): LearningPortfolioState => ({
        ...state,
        deleted: null,
    })),

    on(learningPortfolioActions.clear, (): LearningPortfolioState => initialState),
);
